"use client";

import { CentraJS } from "@frend-digital/centra-js-client";
import type { Token } from "@frend-digital/centra-types";
import { useQuery } from "@tanstack/react-query";
import { ofetch } from "ofetch";
import type { Middleware } from "openapi-fetch";
import React, { useEffect, useMemo } from "react";
import type { CentraGeoJwt } from "../../middleware";
import { createCentraClient } from "../../oas-client";
import { useLocalToken } from "../auth/store";
import { useCentraCheckoutScript } from "../hooks";
import CentraContext from "./centra-context";
import { useLocalStorage } from "./useLocalStorage";

const MUTATION_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

const resumability: Middleware = {
  async onRequest({ request }) {
    if (MUTATION_METHODS.includes(request.method.toUpperCase())) {
      CentraJS.suspend();
    }
    return request;
  },
  async onResponse({ request, response }) {
    if (MUTATION_METHODS.includes(request.method.toUpperCase())) {
      CentraJS.resume();
    }
    return response;
  },
};

const useCreateCentraClient = (apiUrl: string, token: Token) => {
  return useMemo(() => {
    const client = createCentraClient(apiUrl, undefined, {
      headers: {
        "API-Token": token!,
      },
    });

    client.use(resumability);

    return client;
  }, [apiUrl, token]);
};

export const CentraClientProvider = ({
  children,
  overrideToken,
  apiUrl,
}: {
  children: React.ReactNode;
  overrideToken?: string;
  apiUrl: string;
}) => {
  const [giftToken, setGiftToken] = useLocalStorage<string | null>(
    "centra-gift-token",
    null
  );

  const localToken = useLocalToken();

  const centraFetchClient = useCreateCentraClient(
    apiUrl,
    overrideToken || localToken || ""
  );

  return (
    <CentraContext.Provider
      value={{
        apiUrl,
        token: overrideToken || localToken,
        giftToken,
        setGiftToken,
        client: centraFetchClient,
      }}
    >
      {children}
      <InjectCentraCheckoutScript />
    </CentraContext.Provider>
  );
};

export const CentraServerProvider = ({
  children,
  token,
  apiUrl,
}: {
  children: React.ReactNode;
  token: string;
  apiUrl: string;
}) => {
  const [giftToken, setGiftToken] = useLocalStorage<string | null>(
    "centra-gift-token",
    null
  );

  const centraFetchClient = useCreateCentraClient(apiUrl, token);

  return (
    <CentraContext.Provider
      value={{
        token,
        giftToken,
        setGiftToken,
        apiUrl,
        client: centraFetchClient,
      }}
    >
      {children}
      <InjectCentraCheckoutScript />
    </CentraContext.Provider>
  );
};

export const CentraProvider = ({
  token: overrideToken,
  apiUrl,
  children,
  tokenPromise,
}: {
  token?: string;
  tokenPromise?: Promise<string | undefined>;
  apiUrl: string;
  children: React.ReactNode;
}) => {
  const { data: token } = useQuery({
    queryKey: ["userdata"],
    queryFn: async () => {
      if (tokenPromise) {
        const token = await tokenPromise;
        if (token) return token;
      }

      return ofetch<CentraGeoJwt>("/api/centra/geodata").then(
        (res) => res.token
      );
    },
    initialData: overrideToken,
  });

  const [giftToken, setGiftToken] = useLocalStorage<string | null>(
    "centra-gift-token",
    null
  );

  const client = useCreateCentraClient(apiUrl, token || null);

  return (
    <CentraContext.Provider
      value={{
        waitingForToken: !token,
        token: token || null,
        giftToken,
        setGiftToken,
        apiUrl,
        client,
      }}
    >
      {children}
      <InjectCentraCheckoutScript />
    </CentraContext.Provider>
  );
};

const InjectCentraCheckoutScript = () => {
  const { data } = useCentraCheckoutScript();
  useEffect(() => {
    if (!data) return;

    const script = document.createElement("script");
    script.innerHTML = data;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [data]);
  return null;
};
